import { computed } from 'vue';
import { goal as goalConfig } from 'shared/api/query/configs.json';

// Hard cap on buckets per chart. User-entered min/max/bucketSize can otherwise
// produce unbounded arrays (e.g. min=0, max=1_000_000, bucketSize=1) that
// allocate GB of strings and block the main thread.
export const MAX_BUCKETS = 25;

export default function useCustomBuckets(customBucket, resultRaw, groupBy) {
  const defaultAmountOfBuckets = 5;
  const calculateBuckets = (min, max, bucketSize) => {
    const buckets = [
      { min: undefined, max: min, label: `< ${min}` },
    ];
    if (bucketSize > 0 && max > min) {
      const iterations = Math.min(MAX_BUCKETS, Math.ceil((max - min) / bucketSize));
      for (let k = 0; k < iterations; k += 1) {
        const lo = min + k * bucketSize;
        const hi = lo + bucketSize;
        buckets.push({ min: lo, max: hi, label: `${lo} - ${hi}` });
      }
    }
    buckets.push({
      min: buckets[buckets.length - 1].max,
      max: undefined,
      label: `> ${buckets[buckets.length - 1].max}`,
    });
    return buckets;
  };

  const calculatePercentile = (data, percentile) => {
    const values = data.map((item) => item.val).sort((a, b) => a - b);

    const index = Math.ceil(percentile * values.length) - 1;

    return Math.round(values[index]);
  };

  const buckets = computed(() => {
    if (customBucket.value.bucketType === 'automatic') {
      if (groupBy.value.edge === goalConfig.edges.cachedCalculatedCurrent) {
        return calculateBuckets(0, 100, 20);
      }
      const groupByData = resultRaw.value?.res?.[0]?.['@groupby'];
      if (!groupByData || groupByData.length < 2) {
        return calculateBuckets(0, 100, 20);
      }
      const lowerBound = calculatePercentile(groupByData, 0.10);
      const upperBound = calculatePercentile(groupByData, 0.9);
      return calculateBuckets(lowerBound, upperBound, Math.min(groupByData.length - 2, Math.round((upperBound - lowerBound) / (defaultAmountOfBuckets - 2))));
    }
    return calculateBuckets(customBucket.value.min, customBucket.value.max, customBucket.value.bucketSize);
  });

  const findBucket = (value) => {
    if (value < buckets.value[0].max) {
      return buckets.value[0];
    }
    if (value >= buckets.value[buckets.value.length - 1].min) {
      return buckets.value[buckets.value.length - 1];
    }
    const lastNonSentinelIndex = buckets.value.length - 2;
    return buckets.value.find((bucket, index) => {
      const withinUpper = index === lastNonSentinelIndex ? value <= bucket.max : value < bucket.max;
      return value >= bucket.min && withinUpper;
    });
  };

  const findBucketLabel = (value) => {
    const bucket = findBucket(value);
    return bucket?.label;
  };

  return { buckets, findBucket, findBucketLabel };
}
